import { useState, useEffect } from 'react'
import { useFacturacionAgil } from './useFacturacionAgil'
import CabeceraForm from './CabeceraForm'
import DetalleProductos from './DetalleProductos'
import DetalleServicios from './DetalleServicios'
import ClienteCard from './ClienteCard'
import TotalesPanel from './TotalesPanel'
import Shortcuts from './Shortcuts'
import ProcesoCobroModal from './ProcesoCobroModal'
import CobroModal from './CobroModal'
import Icon from '../../components/Icon'
import { useAuth } from '../../auth/AuthContext'
import { useNotify } from '../../components/Notifications'
import { getConfig } from '../../config'
import { formatCurrency } from '../../utils/format'
import * as comprobantesApi from '../../api/comprobantes'
import * as cobrosApi from '../../api/cobros'

export default function FacturarPage() {
  const vm = useFacturacionAgil()
  const { auth } = useAuth()
  const notify = useNotify()
  const cfg = getConfig()

  const [tab, setTab] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [imprimirDirecto, setImprimirDirecto] = useState(() => localStorage.getItem('imprimirDirectoFactAgil') === '1') // eslint-disable-line no-unused-vars

  const [procesoCobroOpen, setProcesoCobroOpen] = useState(false)
  const [cobroOpen, setCobroOpen] = useState(false)
  const [createdComprobante, setCreatedComprobante] = useState(null)
  const [procesoCobro, setProcesoCobro] = useState(null)

  const caja = auth?.caja || vm.timbradoAgil?.puntoEmisionVenta?.caja || null
  const usuario = auth?.accesoSistema?.usuario || null
  const esVirtual = vm.timbradoAgil?.timbradoEmpresa?.claseTimbrado?.codigo === 'timbrado_virtual'

  // --- Submit: validar -> (apertura proceso cobro) -> crear comprobante -> cobro -> SIFEN/KuDE ---
  const handleSubmit = async () => {
    setSubmitted(true)
    const errs = vm.validate()
    if (errs.length) { notify.error(errs[0]); return }
    if (!cfg.emisionHabilitada) { notify.warning('Emisión deshabilitada (config.emisionHabilitada=false).'); return }
    try {
      let pc = null
      if (caja?.id && usuario?.id) pc = await cobrosApi.buscarProcesoCobroAbierto({ cajaId: caja.id, usuarioId: usuario.id })
      if (!pc) { setProcesoCobroOpen(true); return }
      setProcesoCobro(pc)
      await crearComprobante(pc)
    } catch (e) { notify.error(mensajeError(e, 'No se pudo iniciar la facturación.')) }
  }

  const onConfirmProcesoCobro = async ({ montoBaseEfectivo }) => {
    setSubmitting(true)
    try {
      const pc = await cobrosApi.abrirProcesoCobro({ estadoAbiertoId: vm.cobroParams.estadoAbierto?.id, caja, usuario, locacionId: auth?.locacionId, montoBaseEfectivo })
      setProcesoCobro(pc); setProcesoCobroOpen(false)
      await crearComprobante(pc)
    } catch (e) { notify.error(mensajeError(e, 'No se pudo abrir el proceso de cobro.')) } finally { setSubmitting(false) }
  }

  const crearComprobante = async (pc) => {
    setSubmitting(true)
    try {
      const created = await comprobantesApi.crearComprobante(vm.buildPayload(), { facturacionAgil: true })
      const id = created?.id ?? created?.comprobante?.id
      setCreatedComprobante({ ...created, id }); setProcesoCobro(pc || procesoCobro); setCobroOpen(true)
    } catch (e) { notify.error(mensajeError(e, 'No se pudo crear el comprobante.')) } finally { setSubmitting(false) }
  }

  const onConfirmCobro = async ({ formaPago, importe }) => {
    setSubmitting(true)
    try {
      await cobrosApi.guardarCobro({
        caja, cliente: vm.comprobante.cliente, moneda: vm.comprobante.moneda, procesoCobro,
        tipoCobro: vm.cobroParams.tipoCobro, tipoCobroEntidad: vm.cobroParams.tipoCobroEntidad,
        usuario, locacion: auth?.locacion, comprobante: createdComprobante, formaPago, importe,
      })
      if (esVirtual && createdComprobante?.id) await comprobantesApi.generarJsonVirtual(createdComprobante.id)
      const cdc = createdComprobante?.cdc
      if (esVirtual && cfg.serverFE && cdc) window.open(cfg.serverFE + cdc, '_blank')
      notify.success(`Factura ${createdComprobante?.numero || ''} emitida y cobrada correctamente.`)
      setCobroOpen(false); setCreatedComprobante(null); setProcesoCobro(null); setSubmitted(false); vm.reset()
    } catch (e) { notify.error(mensajeError(e, 'No se pudo registrar el cobro.')) } finally { setSubmitting(false) }
  }

  // Atajo F10 = Emitir factura
  useEffect(() => {
    const h = (e) => { if (e.key === 'F10') { e.preventDefault(); handleSubmit() } }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  })

  if (vm.loading) return <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Cargando…</div>

  const lineas = vm.productos.filter((d) => d.producto).length + vm.servicios.filter((d) => d.servicio).length

  return (
    <>
      <div className="page-head">
        <div className="page-title-wrap">
          <h1>Nueva factura</h1>
          <div className="session-bar">
            <span className="chip"><span className="chip-label">Timbrado</span><strong>{vm.timbradoAgil?.timbradoEmpresa?.numeroTimbrado || '—'}</strong></span>
            <span className="chip"><span className="chip-label">Pto. Emisión</span><strong>{vm.timbradoAgil?.puntoEmisionVenta?.numero || '—'}</strong></span>
            <span className="chip green"><span className="chip-label">Caja</span><strong>{caja?.nombre || '—'}</strong></span>
            <span className="chip"><span className="chip-label">Usuario</span><strong>{auth?.username}</strong></span>
            {esVirtual && <span className="chip green"><strong>SIFEN</strong></span>}
          </div>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-sm"><Icon name="info" size={14} /> Ayuda</button>
        </div>
      </div>

      {vm.initError && <div style={{ padding: '14px 24px 0' }}><div className="well-info"><Icon name="info" size={14} />{vm.initError}</div></div>}

      <div className="content">
        <div className="col-left">
          <div className="card">
            <div className="card-head">
              <h3 className="card-title"><span className="num">1</span>Cliente</h3>
              <div className="card-head-actions"><button className="btn btn-sm" onClick={() => vm.changeCliente(null)}>Cliente ocasional</button></div>
            </div>
            <div className="card-body"><ClienteCard vm={vm} submitted={submitted} /></div>
          </div>

          <div className="card">
            <div className="card-head"><h3 className="card-title"><span className="num">2</span>Detalles del comprobante</h3></div>
            <div className="card-body"><CabeceraForm vm={vm} submitted={submitted} /></div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3 className="card-title"><span className="num">3</span>{tab === 0 ? 'Productos' : 'Servicios'}</h3>
              <span className="card-sub" style={{ marginLeft: 4 }}>
                {(tab === 0 ? vm.productos.filter((d) => d.producto) : vm.servicios.filter((d) => d.servicio)).length} líneas
              </span>
              <div className="card-head-actions">
                <div className="seg">
                  <button className={tab === 0 ? 'on' : ''} onClick={() => setTab(0)}>Productos</button>
                  <button className={tab === 1 ? 'on' : ''} onClick={() => setTab(1)}>Servicios</button>
                </div>
              </div>
            </div>
            {tab === 0 ? <DetalleProductos vm={vm} /> : <DetalleServicios vm={vm} />}
          </div>

          <div className="card">
            <div className="card-head">
              <h3 className="card-title"><span className="num">4</span>Condiciones de pago</h3>
              <div className="card-head-actions">
                <div className="seg">
                  <button className={vm.esContado ? 'on' : ''}>Contado</button>
                  <button className={!vm.esContado ? 'on' : ''}>Crédito</button>
                </div>
              </div>
            </div>
            <div className="card-body">
              {vm.esContado ? (
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>El cobro se registra al emitir (efectivo / tarjeta / cheque en el modal de cobro).</span>
              ) : (
                <div className="form-grid">
                  <div className="field col-4">
                    <label className="field-label">Forma de pago</label>
                    <div className="field-input-wrap combobox">
                      <select className="field-input" style={{ appearance: 'none' }} value={vm.comprobante.formaPago?.id ?? ''}
                        onChange={(e) => vm.setField('formaPago', vm.formasPagos.find((f) => String(f.id) === e.target.value) || null)}>
                        <option value="">—</option>
                        {vm.formasPagos.map((f) => <option key={f.id} value={f.id}>{f.descripcion}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-right">
          <TotalesPanel vm={vm} submitting={submitting} onSubmit={handleSubmit} />
          <Shortcuts />
        </div>
      </div>

      <div className="action-bar">
        <div className="action-bar-info">
          <span className="item"><span>Líneas</span><strong>{lineas}</strong></span>
          <span style={{ color: 'var(--border-strong)' }}>·</span>
          <span className="item"><span>Total</span><strong className="mono">{formatCurrency(vm.totales.totalGeneral, vm.comprobante.moneda)}</strong></span>
        </div>
        <div className="action-bar-actions">
          <button className="btn" onClick={() => { vm.reset(); setSubmitted(false) }}><Icon name="close" size={14} /> Cancelar</button>
          <button className="btn btn-primary" disabled={submitting} onClick={handleSubmit}><Icon name="check" size={15} /> Emitir factura <span className="kbd-inline">F10</span></button>
        </div>
      </div>

      <ProcesoCobroModal open={procesoCobroOpen} caja={caja} usuario={auth?.username} loading={submitting} onConfirm={onConfirmProcesoCobro} onClose={() => setProcesoCobroOpen(false)} />
      <CobroModal open={cobroOpen} total={vm.totales.totalGeneral} moneda={vm.comprobante.moneda} formasPago={vm.formasPagos} loading={submitting} onConfirm={onConfirmCobro} onClose={() => setCobroOpen(false)} />
    </>
  )
}

function mensajeError(e, fallback) {
  return e?.response?.data?.[0]?.message || e?.response?.data?.message || e?.response?.data?.error || fallback
}
